<template>
  <div class="notification-bell relative shrink-0 overflow-visible">
    <button
      type="button"
      :class="[
        'app-navbar-icon-btn',
        isCampaignDonate ? 'app-navbar-icon-btn--campaign' : '',
        hasUnreadNotifications && !isCampaignDonate ? 'app-navbar-icon-btn--active' : '',
        hasUnreadNotifications && isCampaignDonate ? 'app-navbar-icon-btn--campaign-active' : '',
      ]"
      @click="toggleDropdown"
    >
      <span class="sr-only">Notifications</span>
      <BellIcon class="h-5 w-5" aria-hidden="true" />
      <span
        v-if="unreadCount > 0"
        class="absolute -right-0.5 -top-0.5 inline-flex min-h-[1.125rem] min-w-[1.125rem] items-center justify-center rounded-full bg-red-500 px-1 text-[0.625rem] font-bold leading-none text-white"
      >
        {{ unreadCount > 99 ? '99+' : unreadCount }}
      </span>
    </button>

    <div v-if="isDropdownOpen" class="app-navbar-notifications">
      <div class="app-navbar-notifications__header">
        <p class="app-navbar-notifications__title">Notifications</p>
        <p v-if="unreadCount > 0" class="app-navbar-notifications__subtitle">
          {{ unreadCount }} unread
        </p>
      </div>

      <div class="max-h-80 overflow-y-auto sm:max-h-96">
        <div v-if="loading" class="px-4 py-8 text-center text-gray-500">
          <div
            class="mx-auto h-6 w-6 animate-spin rounded-full border-2 border-primary/20 border-t-primary"
          />
          <p class="mt-2 text-sm">Loading notifications...</p>
        </div>

        <div
          v-else-if="recentUnreadNotifications.length === 0"
          class="px-4 py-8 text-center text-gray-500"
        >
          <div
            class="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-gray-100 text-gray-400"
          >
            <BellIcon class="h-6 w-6" aria-hidden="true" />
          </div>
          <p class="mt-3 text-sm font-medium text-gray-700">All caught up</p>
          <p class="mt-1 text-xs text-gray-500">No unread notifications</p>
        </div>

        <div v-else>
          <div
            v-for="notification in recentUnreadNotifications"
            :key="notification.id"
            :class="[
              'app-navbar-notifications__item',
              { 'app-navbar-notifications__item--unread': !notification.readStatus },
            ]"
            @click="markAsRead(notification.id)"
          >
            <div class="flex items-start gap-3">
              <div
                :class="[
                  'app-navbar-notifications__icon',
                  notification.readStatus
                    ? 'app-navbar-notifications__icon--read'
                    : 'app-navbar-notifications__icon--unread',
                ]"
              >
                <CurrencyDollarIcon
                  v-if="
                    notification.notificationType === 'qr_code_donation_received' ||
                    notification.notificationType === 'donation_verified_by_donor'
                  "
                  class="h-4 w-4"
                  aria-hidden="true"
                />
                <ClipboardDocumentListIcon
                  v-else-if="
                    notification.notificationType === 'task_assigned' ||
                    notification.notificationType === 'task_completed'
                  "
                  class="h-4 w-4"
                  aria-hidden="true"
                />
                <CurrencyDollarIcon v-else class="h-4 w-4" aria-hidden="true" />
              </div>

              <div class="min-w-0 flex-1">
                <p class="text-sm font-medium text-gray-900">
                  {{ formatNotificationMessage(notification) }}
                </p>
                <template
                  v-if="
                    notification.notificationType === 'qr_code_donation_received' ||
                    notification.notificationType === 'donation_verified_by_donor'
                  "
                >
                  <p class="mt-1 text-xs text-gray-500">
                    Amount: ${{
                      notification.extra?.donationAmount || notification.extra?.amountOfDonation
                    }}
                  </p>
                  <p class="text-xs text-gray-500">
                    Frequency:
                    {{
                      notification.extra?.donationFrequency ||
                      notification.extra?.frequencyOfDonation
                    }}
                  </p>
                </template>
                <template v-if="notification.notificationType === 'task_assigned'">
                  <p v-if="notification.extra?.taskDescription" class="mt-1 text-xs text-gray-500">
                    {{ notification.extra.taskDescription }}
                  </p>
                </template>
                <template v-if="notification.notificationType === 'task_completed'">
                  <p class="mt-1 text-xs text-gray-500">
                    Task: {{ notification.extra?.taskName || 'Untitled Task' }}
                  </p>
                </template>
                <p class="mt-1 text-xs text-gray-400">
                  {{ formatTimestamp(notification.datetimeCreated) }}
                </p>
              </div>

              <div v-if="!notification.readStatus" class="mt-2 shrink-0">
                <span class="block h-2 w-2 rounded-full bg-primary" aria-hidden="true" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="app-navbar-notifications__footer">
        <button
          type="button"
          class="app-navbar-notifications__view-all"
          @click="viewAllNotifications"
        >
          View all notifications
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watchEffect } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { BellIcon, CurrencyDollarIcon } from '@heroicons/vue/24/outline'
import { ClipboardDocumentListIcon } from '@heroicons/vue/20/solid'
import { notificationFunctions } from '@/services/notifications'

export default {
  name: 'NotificationBell',
  components: {
    BellIcon,
    CurrencyDollarIcon,
    ClipboardDocumentListIcon,
  },
  props: {
    isCampaignDonate: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const router = useRouter()
    const store = useStore()

    const { api, loadNotificationsOnce } = notificationFunctions()

    const loading = ref(false)
    const isDropdownOpen = ref(false)
    const notificationCollection = ref(null)

    const isAuthenticated = computed(() => store.getters.isLoggedIn)
    const isFundraiser = computed(() => store.getters.isFundraiser)
    const isCanvassingOrgAdmin = computed(() => store.getters.isCanvassingOrgAdmin)
    const isCanvassingOrgFieldDirector = computed(() => store.getters.isCanvassingOrgFieldDirector)
    const isGivarooStaff = computed(() => store.getters.isGivarooStaff)

    const canViewNotifications = computed(() => {
      return (
        isFundraiser.value ||
        isCanvassingOrgAdmin.value ||
        isCanvassingOrgFieldDirector.value ||
        isGivarooStaff.value
      )
    })

    const recentUnreadNotifications = computed(() => {
      if (!notificationCollection.value?.list) return []

      return notificationCollection.value.list
        .filter((n) => !n.readStatus)
        .sort((a, b) => new Date(b.datetimeCreated) - new Date(a.datetimeCreated))
        .slice(0, 15)
    })

    const unreadCount = computed(() => {
      if (!notificationCollection.value?.list) return 0
      return notificationCollection.value.list.filter((n) => !n.readStatus).length
    })

    const hasUnreadNotifications = computed(() => unreadCount.value > 0)

    const fetchNotifications = async () => {
      if (!isAuthenticated.value || !canViewNotifications.value) return

      try {
        loading.value = true
        notificationCollection.value = await loadNotificationsOnce()
      } catch (err) {
        console.error('Failed to fetch notifications:', err)
      } finally {
        loading.value = false
      }
    }

    const markAsRead = async (notificationId) => {
      try {
        await api.csc.markAsRead({
          id: notificationId,
          readStatus: true,
        })

        const notification = notificationCollection.value.list.find((n) => n.id === notificationId)
        if (notification) {
          notification.readStatus = true
        }

        window.dispatchEvent(
          new CustomEvent('notification-read', {
            detail: { notificationId: notificationId },
          }),
        )
      } catch (err) {
        console.error('Failed to mark notification as read:', err)
      }
    }

    const toggleDropdown = () => {
      isDropdownOpen.value = !isDropdownOpen.value
    }

    const viewAllNotifications = () => {
      isDropdownOpen.value = false
      router.push({ path: '/notifications' })
    }

    const formatNotificationMessage = (notification) => {
      if (notification.notificationType === 'qr_code_donation_received') {
        const { donorFullName, campaignName } = notification.extra || {}
        return `${
          donorFullName || 'Someone'
        } has successfully submitted their donation for ${campaignName} campaign!`
      }
      if (notification.notificationType === 'donation_verified_by_donor') {
        const { donorName, campaignName } = notification.extra || {}
        return `${
          donorName || 'Someone'
        } has successfully verified their donation for ${campaignName} campaign!`
      }
      if (notification.notificationType === 'task_assigned') {
        const { taskName, assignerName } = notification.extra || {}
        return `${assignerName || 'Someone'} assigned you a new task: ${
          taskName || 'Untitled Task'
        }`
      }
      if (notification.notificationType === 'task_completed') {
        const { taskName, assigneeName } = notification.extra || {}
        return `${assigneeName || 'Someone'} has completed the task: ${taskName || 'Untitled Task'}`
      }
      return 'New notification'
    }

    const formatTimestamp = (timestamp) => {
      const date = new Date(timestamp)
      const now = new Date()
      const diffInHours = Math.floor((now - date) / (1000 * 60 * 60))

      if (diffInHours < 1) {
        return 'Just now'
      } else if (diffInHours < 24) {
        return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`
      } else {
        const diffInDays = Math.floor(diffInHours / 24)
        if (diffInDays < 7) {
          return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`
        } else {
          return date.toLocaleDateString()
        }
      }
    }

    const handleClickOutside = (event) => {
      const notificationBell = event.target.closest('.notification-bell')
      if (!notificationBell) {
        isDropdownOpen.value = false
      }
    }

    const handleNotificationRead = (event) => {
      console.log('NotificationBell: Received notification-read event', event.detail)
    }

    const handleNewNotification = () => {
      fetchNotifications()
    }

    watchEffect(() => {
      if (isAuthenticated.value && canViewNotifications.value) {
        fetchNotifications()
      }
    })

    onMounted(() => {
      if (isAuthenticated.value && canViewNotifications.value) {
        fetchNotifications()
        document.addEventListener('click', handleClickOutside)
        window.addEventListener('notification-read', handleNotificationRead)
        window.addEventListener('new-notification', handleNewNotification)
      }
    })

    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside)
      window.removeEventListener('notification-read', handleNotificationRead)
      window.removeEventListener('new-notification', handleNewNotification)
    })

    return {
      recentUnreadNotifications,
      unreadCount,
      loading,
      isDropdownOpen,
      hasUnreadNotifications,
      markAsRead,
      toggleDropdown,
      viewAllNotifications,
      formatNotificationMessage,
      formatTimestamp,
    }
  },
}
</script>

<style scoped>
.notification-bell {
  display: flex;
}
</style>
